import React from 'react';
import { Tooltip } from 'antd';
import {
	DownloadOutlined,
	FilePdfOutlined,
	FileExcelOutlined,
	EyeOutlined,
	RedoOutlined,
} from '@ant-design/icons';
import { Text } from 'atoms';
import { ACL } from 'components/common';

const formatINR = (value) => {
	const n = Number(value);
	if (value == null || Number.isNaN(n)) return '-';
	return `₹${n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

export const DATE_FORMAT = 'DD-MM-YYYY';
export const PaymentStatusOptions = [
	{ value: 'paid', label: 'Paid' },
	{ value: 'partially_paid', label: 'Partially Paid' },
	{ value: 'unpaid', label: 'Unpaid' },
];

export const InvoiceStatusOptions = [
	{ value: 'active', label: 'Active' },
	{ value: 'cancelled', label: 'Cancelled' },
	{ value: 'draft', label: 'Draft' },
];

const STATUS_STYLES = {
	// payment
	paid: { bg: '#e7f6ec', color: '#3f9c53' },
	partially_paid: { bg: '#fcefd9', color: '#e08a2b' },
	unpaid: { bg: '#fdecec', color: '#d84a4a' },
	// invoice
	active: { bg: '#e7f6ec', color: '#3f9c53' },
	cancellation_pending: { bg: '#fcefd9', color: '#e08a2b' },
	cancelled: { bg: '#fdecec', color: '#d84a4a' },
	draft: { bg: '#f0f1f3', color: '#6b7280' },
};
const DEFAULT_STATUS_STYLE = { bg: '#f0f1f3', color: '#6b7280' };

const normaliseStatus = (value) =>
	(value || '').toString().trim().toLowerCase().replace(/\s+/g, '_');

const toLabel = (value) =>
	(value || '')
		.toString()
		.split(/[_\s]+/)
		.map((w) => (w ? w.charAt(0).toUpperCase() + w.slice(1) : ''))
		.join(' ');

const renderStatusBadge = (status) => {
	if (status == null || status === '') return <Text>-</Text>;
	const { bg, color } = STATUS_STYLES[normaliseStatus(status)] || DEFAULT_STATUS_STYLE;
	return (
		<span
			style={{
				display: 'inline-block',
				background: bg,
				color,
				padding: '4px 14px',
				borderRadius: 8,
				fontSize: 13,
				fontWeight: 600,
				lineHeight: 1.5,
				whiteSpace: 'nowrap',
			}}
		>
			{toLabel(status)}
		</span>
	);
};

const iconBtnStyle = { fontSize: 16, cursor: 'pointer' };

export const getTableColumns = ({
	onView,
	onDownload,
	onPdf,
	onExport,
	onRegenerate,
	showRegenerateButton = true,
	showInvoiceStatus = true,
}) => [
	{
		title: 'Invoice No',
		dataIndex: 'invoice_no',
		key: 'invoice_no',
		render: (text) => <Text style={{ fontWeight: 600 }}>{text || '-'}</Text>,
	},
	{
		title: 'Billing Period',
		dataIndex: 'billing_period',
		key: 'billing_period',
		render: (text) => <Text>{text || '-'}</Text>,
	},
	{
		title: 'Revision',
		dataIndex: 'version',
		key: 'version',
		width: 80,
		render: (text) => <Text>{text ?? '-'}</Text>,
	},
	{
		title: 'Client',
		dataIndex: 'client',
		key: 'client',
		render: (text) => <Text>{text || '-'}</Text>,
	},
	{
		title: 'Project',
		dataIndex: 'project',
		key: 'project',
		render: (text) => <Text>{text || '-'}</Text>,
	},
	{
		title: 'Shipping',
		dataIndex: 'shipping_method',
		key: 'shipping_method',
		render: (text) => <Text>{Array.isArray(text) ? text.join(', ') : text || '-'}</Text>,
	},
	{
		title: 'No of Trips',
		dataIndex: 'no_of_trips',
		key: 'no_of_trips',
		width: 90,
		render: (text) => <Text>{text ?? '-'}</Text>,
	},
	{
		title: 'Invoice Amount',
		dataIndex: 'subtotal',
		key: 'subtotal',
		render: (text) => <Text>{formatINR(text)}</Text>,
	},
	{
		title: 'Paid Amount',
		dataIndex: 'paid',
		key: 'paid',
		render: (text) => <Text>{formatINR(text)}</Text>,
	},
	{
		title: 'Tds Amount',
		dataIndex: 'tds_amount',
		key: 'tds_amount',
		render: (text) => <Text>{formatINR(text)}</Text>,
	},
	{
		title: 'Total Payable',
		dataIndex: 'total_payable',
		key: 'total_payable',
		render: (text) => <Text>{formatINR(text)}</Text>,
	},
	{
		title: 'Payment Status',
		dataIndex: 'payment_status',
		key: 'payment_status',
		render: renderStatusBadge,
	},
	...(showInvoiceStatus
		? [
				{
					title: 'Invoice Status',
					dataIndex: 'invoice_status',
					key: 'invoice_status',
					render: renderStatusBadge,
				},
		  ]
		: []),
	{
		title: 'Actions',
		key: 'actions',
		width: 170,
		render: (_, record) => (
			<div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
				<Tooltip title="View">
					<EyeOutlined
						style={{ ...iconBtnStyle, color: '#2563eb' }}
						onClick={() => onView?.(record)}
					/>
				</Tooltip>
				{showRegenerateButton && (
					<ACL action="update" module="client_invoice_regenerate">
						<Tooltip title="Regenerate Invoice">
							<RedoOutlined
								style={{ ...iconBtnStyle, color: '#9d43b3' }}
								onClick={() => onRegenerate?.(record)}
							/>
						</Tooltip>
					</ACL>
				)}
				<Tooltip title="Download Invoice">
					<DownloadOutlined
						style={{ ...iconBtnStyle, color: '#475569' }}
						onClick={() => onDownload?.(record)}
					/>
				</Tooltip>
				<Tooltip title="View PDF">
					<FilePdfOutlined
						style={{ ...iconBtnStyle, color: '#ef4444' }}
						onClick={() => onPdf?.(record)}
					/>
				</Tooltip>
				<Tooltip title="Export Invoice">
					<FileExcelOutlined
						style={{ ...iconBtnStyle, color: '#16a34a' }}
						onClick={() => onExport?.(record)}
					/>
				</Tooltip>
			</div>
		),
	},
];
